import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AdminDashboard from "./admindashboard";
import ProcessAppointmentPopup from "./processAppointmentPopup";
import RefundAmount from "./refundAmount";
import { Pagination } from "react-bootstrap";

function PaymentGateway() {
  const [loading, setLoading] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datesInitialized, setDatesInitialized] = useState(false);

  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  // Format date to YYYY-MM-DD
  const formatDateToYMD = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  // Set default dates on mount
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    setFromDate(formatDateToYMD(firstDay));
    setToDate(formatDateToYMD(today));
    setDatesInitialized(true);
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (datesInitialized) {
      setCurrentPage(0);
    }
  }, [fromDate, toDate, searchTerm, datesInitialized]);

  // Fetch data when page changes or dates are initialized
  useEffect(() => {
    if (datesInitialized && fromDate && toDate) {
      getData(currentPage);
    }
  }, [currentPage, fromDate, toDate, datesInitialized]);

  const getData = async (pageNumber = 0) => {
    // Don't make API call if dates are not set
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true);
      const payload = { fromDate, toDate };
      const response = await axios.post(
        "/api/payment/getAllPaymentsWithStatus",
        payload,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserPayments(response.data.content || []);
      setCurrentPage(response.data.number);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isProcessAppointment, setIsProcessAppointment] = useState(false);
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);

  const handleProcessAppointment = (payment) => {
    setSelectedPayment(payment);
    setIsProcessAppointment(true);
  };

  const handleCancelProcessAppointment = () => {
    setIsProcessAppointment(false);
    getData(currentPage);
  };

  const handleCancelClick = (payment) => {
    setSelectedPayment(payment);
    setIsCancelPopupOpen(true);
  };

  const handleCloseCancelPopup = () => {
    setSelectedPayment(null);
    setIsCancelPopupOpen(false);
    getData(currentPage);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxToShow = 5;
    let start = Math.max(0, currentPage - 2);
    let end = Math.min(totalPages - 1, start + maxToShow - 1);

    // Adjust start if we're near the end
    if (currentPage >= totalPages - 3) {
      start = Math.max(0, totalPages - maxToShow);
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const handleError = (error) => {
    if (error.response?.status === 401) {
      toast.error("Your session has expired. Please login.");
      navigate("/signin");
    } else {
      toast.error(error.response?.data?.message || error.response?.data?.code || error.message || "An error occurred.");
    }
  };

  return (
    <div>
      <AdminDashboard />
      {loading && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10">
        <div className="card card-raised">
          <div className="card-body">
            <div className="card-title d-flex justify-content-between align-items-center mb-4">
              <span>Payment Gateway Details</span>
            </div>

            <div className="d-flex flex-wrap align-items-end mb-3 gap-3">
              <div>
                <label className="form-label mb-1">From Date:</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={fromDate} 
                  onChange={(e) => setFromDate(e.target.value)} 
                />
              </div>
              <div>
                <label className="form-label mb-1">To Date:</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={toDate} 
                  onChange={(e) => setToDate(e.target.value)} 
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr className="table-header">
                    <th>Sl.No.</th>
                    <th>Company Name</th>
                    <th>Employee Name</th>
                    <th>Date</th>
                    <th>Order Id</th>
                    <th>Payment Id</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {userPayments.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        {loading ? "Loading..." : "No Data Available"}
                      </td>
                    </tr>
                  ) : (
                    userPayments.map((payment, index) => (
                      <tr key={payment.paymentId || index}>
                        <td>{currentPage * 10 + index + 1}</td>
                        <td>{payment.company?.companyname || 'N/A'}</td>
                        <td>
                          <div>
                            {payment.employee?.name} {payment.employee?.lastname}
                          </div>
                          {payment.memberName && (
                            <div className="text-muted small">{payment.memberName}</div>
                          )}
                        </td>
                        <td>{payment.date || 'N/A'}</td>
                        <td>{payment.orderId || 'N/A'}</td>
                        <td>{payment.paymentId || 'N/A'}</td>
                        <td>₹ {payment.amount || 0}</td>
                        <td>
                          <div className="mb-2">
                            <strong>{payment.paymentStatus}</strong>
                          </div>
                          {payment.paymentStatus === "CAPTURED" && payment.appointmentStatus !== "APPOINTMENT COMPLETED" && (
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleProcessAppointment(payment)}
                              >
                                VALIDATE
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleCancelClick(payment)}
                              >
                                REFUND
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 0 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                  {renderPageNumbers()}
                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                  <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                </Pagination>
              </div>
            )}

            {isProcessAppointment && (
              <ProcessAppointmentPopup
                isOpen={isProcessAppointment}
                onRequestClose={handleCancelProcessAppointment}
                payment={selectedPayment}
              />
            )}

            {isCancelPopupOpen && (
              <RefundAmount
                isOpen={isCancelPopupOpen}
                onRequestClose={handleCloseCancelPopup}
                payment={selectedPayment}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentGateway;